import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LicenseBadge from "./LicenseBadge";
import { Settings, Bell, BellOff, Trash2, Hash, Pencil } from "lucide-react";

function Toggle({ checked, onChange, label, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between w-full py-2"
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-gold" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-black transition-transform ${checked ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}

function ConfigPanel({ group, onClose, onSaved }) {
  const c = group.config || {};
  const cleanNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : String(fallback);
  };
  const [cfg, setCfg] = useState({
    chatId: group.chatId || "",
    name: group.name || "",
    notify: !!c.notify,
    notifyScope: c.notifyScope || "both",
    notifyMode: c.notifyMode || "instant",
    notifyTime: cleanNumber(c.notifyTime, 1),
    memberNotice: !!c.memberNotice,
    warNotify: !!c.warNotify,
    warNotifyTime: cleanNumber(c.warNotifyTime, 1),
    commentsAttacks: !!c.commentsAttacks,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.updateConfig(group.chatId, {
        ...cfg,
        chatId: cfg.chatId.trim(),
        name: cfg.name.trim(),
        notifyTime: Math.max(1, Math.min(24, parseInt(cfg.notifyTime || "1", 10) || 1)),
        warNotifyTime: Math.max(1, Math.min(23, parseInt(cfg.warNotifyTime || "1", 10) || 1)),
      });
      toast.success("Configuración guardada");
      if (res.chatIdTestMessage && res.chatIdTestMessage.status !== "sent") {
        toast.warning("Chat ID actualizado, pero no se pudo verificar la conexión del bot en ese grupo");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const selCls = "bg-obsidian border border-white/10 rounded-md text-white px-3 py-1.5 text-sm focus:border-gold outline-none";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface w-full max-w-md max-h-[85vh] flex flex-col p-6" onClick={(e) => e.stopPropagation()} data-testid="config-panel">
        <span className="overline text-gold shrink-0">Configuración de grupo</span>
        <h3 className="text-xl font-heading font-semibold mb-1 shrink-0">{group.name || "Grupo"}</h3>
        <p className="text-xs text-muted-foreground font-mono mb-1 shrink-0">{group.clanTag || "sin clan"}</p>
        {group.clanName && <p className="text-sm text-white/90 mb-4 shrink-0">🏰 {group.clanName}</p>}

        <div className="overflow-y-auto flex-1 pr-1 -mr-1" data-testid="config-panel-scroll">
        <label className="block py-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2"><Hash size={14} /> Chat ID</span>
          <input
            data-testid="cfg-chat-id"
            type="text"
            value={cfg.chatId}
            onChange={(e) => set("chatId", e.target.value)}
            placeholder="120363408946512315@g.us"
            maxLength={40}
            className={`${selCls} w-full font-mono text-xs`}
          />
          <span className="text-xs text-muted-foreground/70 mt-1 block">Formato: numero@g.us. Cambiarlo reasigna el bot a otro chat de WhatsApp.</span>
        </label>

        <label className="block py-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2"><Pencil size={14} /> Nombre visible del grupo</span>
          <input
            data-testid="cfg-group-name"
            type="text"
            value={cfg.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ej. AMAZING CLAN"
            maxLength={80}
            className={`${selCls} w-full`}
          />
        </label>

        <Toggle testid="cfg-notify" checked={cfg.notify} onChange={(v) => set("notify", v)} label="Avisos de ataques" />
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-muted-foreground">Alcance</span>
          <select data-testid="cfg-scope" value={cfg.notifyScope} onChange={(e) => set("notifyScope", e.target.value)} className={selCls}>
            <option value="allies">Solo aliados</option>
            <option value="both">Ambos (aliados + rival)</option>
          </select>
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-muted-foreground">Modo</span>
          <select data-testid="cfg-mode" value={cfg.notifyMode} onChange={(e) => set("notifyMode", e.target.value)} className={selCls}>
            <option value="instant">Instantáneo</option>
            <option value="summary">Resumen</option>
          </select>
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-muted-foreground">Intervalo resumen (horas)</span>
          <input
            data-testid="cfg-time"
            type="number" min="1" max="24"
            value={cfg.notifyTime}
            onFocus={(e) => e.target.select()}
            onChange={(e) => set("notifyTime", e.target.value.replace(/\D/g, ""))}
            onBlur={() => set("notifyTime", String(Math.max(1, Math.min(24, parseInt(cfg.notifyTime || "1", 10) || 1))))}
            className={`${selCls} w-20`}
          />
        </div>
        <Toggle testid="cfg-member" checked={cfg.memberNotice} onChange={(v) => set("memberNotice", v)} label="Avisos de nuevos miembros" />

        <div className="border-t border-white/10 my-3" />
        <Toggle testid="cfg-warnotify" checked={cfg.warNotify} onChange={(v) => set("warNotify", v)} label="Avisos de guerra/CWL (inicio, fin y recordatorios)" />
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-muted-foreground">Recordatorio guerra (horas antes)</span>
          <input
            data-testid="cfg-wartime"
            type="number" min="1" max="23"
            value={cfg.warNotifyTime}
            onFocus={(e) => e.target.select()}
            onChange={(e) => set("warNotifyTime", e.target.value.replace(/\D/g, ""))}
            onBlur={() => set("warNotifyTime", String(Math.max(1, Math.min(23, parseInt(cfg.warNotifyTime || "1", 10) || 1))))}
            className={`${selCls} w-20`}
          />
        </div>

        <div className="border-t border-white/10 my-3" />
        <Toggle testid="cfg-comments" checked={cfg.commentsAttacks} onChange={(v) => set("commentsAttacks", v)} label="Comentarios automáticos en ataques del clan (por estrellas)" />
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-white/10 shrink-0">
          <button onClick={onClose} className="flex-1 border border-white/20 rounded-md py-2 text-sm hover:bg-white/5 transition-all">Cancelar</button>
          <button data-testid="cfg-save" onClick={save} disabled={saving} className="flex-1 bg-gold text-black font-bold rounded-md py-2 text-sm hover:brightness-95 transition-all disabled:opacity-60">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GroupsView({ groups, reload }) {
  const [editing, setEditing] = useState(null);

  const deactivate = async (chatId) => {
    try {
      await api.deactivate(chatId);
      toast.success("Plan desactivado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const removeGroup = async (chatId, name) => {
    if (!window.confirm(`¿Eliminar la membresía de "${name || chatId}"? Se borrará el grupo, su clan y su configuración. Esta acción no se puede deshacer.`)) return;
    try {
      await api.deleteGroup(chatId);
      toast.success("Membresía eliminada");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="overline text-gold">Grupos vinculados</span>
        <h2 className="text-2xl md:text-3xl font-heading font-bold">Grupos de WhatsApp</h2>
      </div>

      {groups.length === 0 ? (
        <div className="card-surface p-12 text-center text-muted-foreground" data-testid="groups-empty">
          <Hash className="mx-auto mb-3 opacity-40" size={40} />
          Aún no hay grupos registrados. Agrega el bot a un grupo y ejecuta un comando,
          o activa una licencia desde la pestaña <span className="text-gold">Licencias</span>.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="groups-list">
          {groups.map((g) => {
            const ls = g.licenseState || {};
            const cfg = g.config || {};
            return (
              <div key={g.chatId} className="card-surface card-hover p-5" data-testid={`group-card-${g.chatId}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold truncate" data-testid={`group-name-${g.chatId}`}>{g.name || "Grupo sin nombre"}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{g.chatId}</div>
                  </div>
                  <LicenseBadge state={ls.status} />
                </div>
                <div className="flex items-center gap-2 text-sm mt-3">
                  <Hash size={14} className="text-gold" />
                  <span className="font-mono">{g.clanTag || "sin clan asignado"}</span>
                </div>
                {g.clanName && (
                  <div className="text-sm text-white/90 mt-1 truncate" data-testid={`clan-name-${g.chatId}`}>
                    🏰 {g.clanName}
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                  {ls.active ? `⏳ ${ls.remainingText || `${ls.daysLeft} día(s)`} restantes` : ls.status === "expired" ? "Membresía expirada" : "Sin plan"}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {cfg.notify ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Bell size={12} /> Avisos {cfg.notifyMode === "summary" ? "resumen" : "instant"}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><BellOff size={12} /> Sin avisos</span>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button data-testid={`config-btn-${g.chatId}`} onClick={() => setEditing(g)} className="flex-1 border border-white/15 rounded-md py-1.5 text-xs flex items-center justify-center gap-1 hover:bg-white/5 transition-all">
                    <Settings size={14} /> Configurar
                  </button>
                  {ls.active && (
                    <button data-testid={`deactivate-btn-${g.chatId}`} onClick={() => deactivate(g.chatId)} title="Desactivar plan" className="border border-crimson/40 text-crimson rounded-md py-1.5 px-3 text-xs flex items-center gap-1 hover:bg-crimson/10 transition-all">
                      <BellOff size={14} />
                    </button>
                  )}
                  <button data-testid={`delete-group-btn-${g.chatId}`} onClick={() => removeGroup(g.chatId, g.name)} title="Eliminar membresía" className="border border-crimson/40 text-crimson rounded-md py-1.5 px-3 text-xs flex items-center gap-1 hover:bg-crimson/10 transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <ConfigPanel group={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
}
